import React, { useEffect, useState } from 'react';
import relatorioService, { type PrestacaoAlcanceMeta } from '../../services/relatorioPrestacaoContasService';
import DownloadButtons from './DownloadButtons';
import Button from '../../components/ui/Button';
import { Table, Tbody, Td, Th, Thead, Tr } from '../../components/ui/Table';
import { toUpperText } from '../../utils/formatters';
import {
  bodyTextClass, inputClass, labelTextClass, primaryButtonClass, strongTextClass,
  subtleSurfaceClass, surfaceClass, type SectionProps,
} from './shared';

type Linha = { quantidadeRealizada: string; percentualAlcance: string; meioAfericao: string; justificativa: string };

const AJUDA_ACOES =
  'Descrever as atividades práticas que foram realizadas no período de aplicação desta prestação de contas para alcance do objeto proposto. Exemplos desta atividade são aqueles propostas nas etapas do plano de trabalho.';
const AJUDA_QUANTIDADE = 'Citar a quantidade realizada no período de aplicação desta prestação de contas.';
const AJUDA_PERCENTUAL = 'Citar o percentual de atendimento da meta (0 a 100).';
const AJUDA_MEIO =
  'Citar os meios de aferição contidos na prestação de contas para comprovação do cumprimento da meta realizada — devem ser comprovados nos anexos deste relatório.';
const AJUDA_JUSTIFICATIVA =
  'Preencher somente no caso de não cumprimento da meta proposta, ou seja, quando a quantidade cumprida não atingiu o previsto no Plano de Trabalho. A justificativa deve ser embasada, com fundamentação técnica. Lembre-se de que o valor financeiro repassado teve por base o cumprimento da meta proposta.';

const Anexo6AlcanceMetasSection: React.FC<SectionProps> = ({ pcId, podeEditar, onAlterado }) => {
  const [alcances, setAlcances] = useState<PrestacaoAlcanceMeta[]>([]);
  const [form, setForm] = useState<Record<string, Linha>>({});
  const [acoes, setAcoes] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);

  const carregar = async () => {
    setLoading(true);
    setError(null);
    try {
      const [dados, relato] = await Promise.all([
        relatorioService.getAlcanceMetas(pcId),
        relatorioService.getRelatoNarrativo(pcId),
      ]);
      setAlcances(dados);
      setAcoes(relato.acoesDesenvolvidasPeriodo || '');
      setForm(Object.fromEntries(dados.map((a) => [a.metaId, {
        quantidadeRealizada: a.quantidadeRealizada ?? '',
        percentualAlcance: a.percentualAlcance != null ? String(Math.round(Number(a.percentualAlcance))) : '',
        meioAfericao: a.meioAfericao || '',
        justificativa: a.justificativa || '',
      }])));
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao carregar o relatório final das metas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pcId]);

  const atualizarLinha = (metaId: string, patch: Partial<Linha>) => {
    setSalvo(false);
    setForm((prev) => ({ ...prev, [metaId]: { ...prev[metaId], ...patch } }));
  };

  const salvar = async () => {
    setSaving(true);
    setError(null);
    setSalvo(false);
    try {
      // O texto das ações vive no mesmo registro do Anexo 3: reenvia os demais campos como estão.
      const relato = await relatorioService.getRelatoNarrativo(pcId);
      await relatorioService.salvarRelatoNarrativo(pcId, {
        acoesExecutadas: relato.acoesExecutadas || '',
        alcanceObjetivos: relato.alcanceObjetivos || '',
        conclusao: relato.conclusao || '',
        justificativaAtrasos: relato.justificativaAtrasos || '',
        medidasAdotadas: relato.medidasAdotadas || '',
        acoesDesenvolvidasPeriodo: acoes,
        finalizar: false,
      });
      const itens = alcances.map((a) => {
        const linha = form[a.metaId];
        return {
          metaId: a.metaId,
          quantidadeRealizada: linha?.quantidadeRealizada?.trim() || null,
          percentualAlcance: linha?.percentualAlcance !== '' && linha?.percentualAlcance != null
            ? Number(linha.percentualAlcance) : null,
          meioAfericao: linha?.meioAfericao || '',
          justificativa: linha?.justificativa || '',
        };
      });
      await relatorioService.salvarAlcanceMetas(pcId, itens);
      await carregar();
      setSalvo(true);
      onAlterado?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao salvar o relatório final.');
    } finally {
      setSaving(false);
    }
  };

  const precisaJustificar = (metaId: string) => {
    const p = form[metaId]?.percentualAlcance;
    return p !== '' && p != null && Number(p) < 100;
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h2 className={`text-lg font-semibold ${strongTextClass}`}>Anexo 6 · Relatório Final do Cumprimento do Objeto e das Metas</h2>
        <DownloadButtons pcId={pcId} anexo="6" nomeArquivo="anexo-6-relatorio-final" />
      </div>

      <p className={`text-sm ${labelTextClass}`}>Exigido apenas na prestação de contas final/única.</p>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <p className={`text-sm ${bodyTextClass}`}>Carregando...</p>
      ) : (
        <>
          <div>
            <label className={`mb-1 block text-xs font-semibold uppercase tracking-[0.1em] ${labelTextClass}`}>
              Ações desenvolvidas, no período, para cumprimento do objeto
            </label>
            <p className={`mb-2 text-xs ${labelTextClass}`}>{AJUDA_ACOES}</p>
            <textarea className={inputClass} rows={4} value={acoes} disabled={!podeEditar}
              onChange={(e) => { setSalvo(false); setAcoes(toUpperText(e.target.value)); }} />
          </div>

          {alcances.length === 0 && (
            <p className={`text-sm ${labelTextClass}`}>Nenhuma meta cadastrada no plano de trabalho.</p>
          )}

          {alcances.map((a) => (
            <div key={a.metaId} className={`space-y-3 rounded-2xl border p-4 ${surfaceClass}`}>
              <div className={`rounded-xl border px-3 py-2 ${subtleSurfaceClass}`}>
                <p className={`text-xs font-semibold uppercase tracking-[0.1em] ${labelTextClass}`}>Descrição da meta (do plano de trabalho)</p>
                <p className={`text-sm font-medium ${strongTextClass}`}>{a.metaTitulo}</p>
              </div>

              <div className="grid gap-3 md:grid-cols-[1fr_140px]">
                <div>
                  <label className={`mb-1 block text-xs font-semibold ${labelTextClass}`}>Quantidade</label>
                  <p className={`mb-1 text-xs ${labelTextClass}`}>{AJUDA_QUANTIDADE}</p>
                  <input className={inputClass} value={form[a.metaId]?.quantidadeRealizada || ''} disabled={!podeEditar}
                    onChange={(e) => atualizarLinha(a.metaId, { quantidadeRealizada: toUpperText(e.target.value) })} />
                </div>
                <div>
                  <label className={`mb-1 block text-xs font-semibold ${labelTextClass}`}>% de alcance</label>
                  <p className={`mb-1 text-xs ${labelTextClass}`}>{AJUDA_PERCENTUAL}</p>
                  <input type="number" min={0} max={100} step={1} className={`${inputClass} text-right`}
                    value={form[a.metaId]?.percentualAlcance || ''} disabled={!podeEditar}
                    onChange={(e) => atualizarLinha(a.metaId, { percentualAlcance: e.target.value.replace(/\D/g, '').slice(0, 3) })} />
                </div>
              </div>

              <div>
                <label className={`mb-1 block text-xs font-semibold ${labelTextClass}`}>Meios para aferição</label>
                <p className={`mb-1 text-xs ${labelTextClass}`}>{AJUDA_MEIO}</p>
                <textarea className={inputClass} rows={2} value={form[a.metaId]?.meioAfericao || ''} disabled={!podeEditar}
                  onChange={(e) => atualizarLinha(a.metaId, { meioAfericao: toUpperText(e.target.value) })} />
              </div>

              <div>
                <label className={`mb-1 block text-xs font-semibold ${labelTextClass}`}>
                  Justificativa {precisaJustificar(a.metaId) ? '(obrigatória: meta abaixo de 100%)' : '(caso as metas propostas não tenham sido alcançadas)'}
                </label>
                <p className={`mb-1 text-xs ${labelTextClass}`}>{AJUDA_JUSTIFICATIVA}</p>
                <textarea className={inputClass} rows={3} value={form[a.metaId]?.justificativa || ''} disabled={!podeEditar}
                  onChange={(e) => atualizarLinha(a.metaId, { justificativa: toUpperText(e.target.value) })} />
              </div>
            </div>
          ))}

          {podeEditar && (
            <div className="flex items-center gap-3">
              <button type="button" onClick={salvar} disabled={saving}
                className={`rounded-xl px-4 py-2 text-sm font-semibold transition disabled:opacity-50 ${primaryButtonClass}`}>
                {saving ? 'Salvando...' : 'Salvar relatório final'}
              </button>
              {salvo && <span className="text-sm text-emerald-700 dark:text-emerald-300">Salvo.</span>}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Anexo6AlcanceMetasSection;
